import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { Select, Space } from 'antd';
import { ReactComponent as IconRefresh } from 'assets/images/icons/iconRefresh.svg';
import { FormDatePicker } from 'components/Custom/datePicker';
import { FormInput } from 'components/Custom/inputs';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import { MessageStates } from 'containers/MessageBox';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import { AccountType } from 'store/app/enums';
import { EntityAccount } from 'store/organisation/models';
import {
  getOrgLoading,
  getSelectedEntityAccounts,
} from 'store/organisation/selectors';
import { JournalPostData, VatRunData } from 'store/vat/models';
import { JournalPostDataLine } from 'store/vat/models/journalPostData';
import { getVatLoading } from 'store/vat/selectors';
import styled from 'styled-components';
import { useNumberFormat } from 'utils/numberFormat';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
} from 'utils/preferences';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 25px;

  .page-title {
    font-weight: 600;
    font-size: 20px;
    line-height: 20px;
    color: var(--color-text-dark, #042a2a);
  }
  .page-subtitle {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: var(--color-text-dark, #042a2a);
  }
  .journal-info-wrapper {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }
  .currency-box {
    height: 45px;
    border: 1px solid var(--color-border, #dbeae3);
    color: var(--color-text-dark, #042a2a);
    font-weight: 600;
    background: var(--color-surface, #f3f7f8);
    padding: 10px 15px;
  }

  .journal-table {
    border: 1px solid var(--color-border, #dbeae3);
    border-spacing: 0;
    width: 100%;

    .table-row {
      height: 55px;
      background: var(--color-surface-raised, #ffffff);
    }

    .table-header {
      height: 45px;
      background: var(--color-layout-bg, #e9f6f7);
    }

    .bl {
      border-left: 1px solid var(--color-border, #dbeae3);
    }

    .bb {
      border-bottom: 1px solid var(--color-border, #dbeae3);
    }
  }

  .buttons {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  .ant-select-selection-item {
    font-weight: 400;
  }
`;

export enum PostJournalFormAction {
  SUBMIT = 'SUBMIT',
  POST = 'POST',
  CANCEL = 'CANCEL',
}

interface PostJournalFormProps {
  data: VatRunData;
  handleSubmit: (
    action: PostJournalFormAction,
    journalPostData?: JournalPostData
  ) => void;
  reconciliation?: Map<string, string>;
  receivableAmount?: number;
  payableAmount?: number;
  refreshAccounts?: () => void;
  handlePreferences: (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => any;
  providerName?: string;
  inputTax: number;
  outputTax: number;
}

const SYSTEM_ACCOUNTS = [
  { systemAccount: 'Accounts Payable', allowTransactions: false },
  { systemAccount: 'Accounts Receivable', allowTransactions: false },
  { systemAccount: 'Bank Revaluation', allowTransactions: false },
  { systemAccount: 'Current Earnings', allowTransactions: false },
  { systemAccount: 'GST', allowTransactions: true },
  { systemAccount: 'Historical Adjustments', allowTransactions: true },
  { systemAccount: 'Realised Currency Gains', allowTransactions: true },
  { systemAccount: 'Retained Earnings', allowTransactions: true },
  { systemAccount: 'Rounding', allowTransactions: true },
  { systemAccount: 'Tracking Transfers', allowTransactions: false },
  { systemAccount: 'Unpaid Expense Claims', allowTransactions: false },
  { systemAccount: 'Unrealised Currency Gains', allowTransactions: false },
  { systemAccount: 'Wage Payables', allowTransactions: false },
];
const MANUAL_POST_USER_ACCOUNT = 'manualPostUserAccount';

export const PostJournalForm: React.FC<PostJournalFormProps> = ({
  handleSubmit,
  data,
  reconciliation,
  receivableAmount = 0,
  payableAmount = 0,
  refreshAccounts,
  handlePreferences,
  providerName,
  inputTax,
  outputTax,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { formatNumber } = useNumberFormat();
  const selectedEntityAccounts = useSelector(getSelectedEntityAccounts);
  const loading = useSelector(getOrgLoading);
  const manualJournalPostLoading = useSelector(getVatLoading);

  const [entityAccounts, setEntityAccounts] = useState<EntityAccount[]>([]);
  const [journalPostData, setJournalPostData] = useState<JournalPostData>();
  const [vatControlAccounts, setVatControlAccounts] = useState<any>([]);

  const { controlAccountCode } = useMemo(() => {
    let name = '';
    let code = '';
    let accountId = '';

    if (reconciliation) {
      name = reconciliation['accountName'];
      code = reconciliation['accountCode'];
      accountId = reconciliation['accountId'];
    }

    return {
      controlAccountName: name,
      controlAccountCode: code,
      primaryAccountId: accountId,
    };
  }, [reconciliation]);
  const savePreference = (account: string) => {
    account &&
      handlePreferences(
        PreferenceSections.VAT_REPORT,
        PreferenceActions.SET,

        {
          name: MANUAL_POST_USER_ACCOUNT + data.tokenId.replaceAll('-', '_'),
          value: account,
        }
      );
  };

  const getPreference = () => {
    return handlePreferences(
      PreferenceSections.VAT_REPORT,
      PreferenceActions.GET,
      {
        name: MANUAL_POST_USER_ACCOUNT + data.tokenId.replaceAll('-', '_'),
      }
    );
  };
  const validateForm = () => {
    if (!journalPostData) return;

    const secondaryAccountLine = journalPostData?.lines
      ? journalPostData.lines[journalPostData.lines.length - 1]
      : null;
    const secondaryAccountEmpty =
      secondaryAccountLine == null || !secondaryAccountLine.accountId;

    const validationErrors = {
      journalDate:
        !journalPostData.journalDate &&
        t('vat-report.please-select-date-to-post-manual-journal'),
      narration:
        !journalPostData.narration &&
        t('vat-report.please-enter-narration-for-manual-journal'),
      secondaryAccount:
        secondaryAccountEmpty &&
        t('vat-report.please-select-account-before-posting-manual-journal'),
    };

    const error = Object.values(validationErrors).find(Boolean);

    if (error) {
      dispatch(
        turnMessageOn({
          title: t('common.error'),
          description: error,
          type: MessageStates.ERROR,
        })
      );
      return;
    }

    handleSubmit(PostJournalFormAction.POST, journalPostData);
    savePreference(journalPostData.secondaryAccountCode || '');
  };

  useEffect(() => {
    const savedAccountCode = getPreference();
    let accId;
    if (savedAccountCode) {
      accId = entityAccounts?.find(
        (f) => f.code === savedAccountCode
      )?.accountID;
    }

    const mjLines: JournalPostDataLine[] = [];

    vatControlAccounts?.forEach((f) => {
      mjLines.push({
        amount: f.amount,
        accountId: f.accountID,
        accountCode: f.code,
        credit: f.credit,
        debit: f.debit,
      });
    });

    mjLines.push({
      amount: payableAmount !== 0 ? payableAmount * -1 : receivableAmount,
      accountId: savedAccountCode && accId ? accId : '',
      accountCode: savedAccountCode && accId ? savedAccountCode : '',
    });

    const initialJournalData: JournalPostData = {
      submittedDate: moment().startOf('day').format('DD/MM/YYYY HH:mm:ss'),
      tokenId: data.tokenId,
      journalDate: dayjs(data.toDate).add(1, 'day').format('YYYY-MM-DD'),
      narration: `VAT ${
        receivableAmount === 0 ? 'Payable' : 'Receivable'
      } for the period ${data.generationPeriod}`,
      lines: mjLines,
    };

    setJournalPostData(initialJournalData);
  }, [
    data,
    receivableAmount,
    payableAmount,
    controlAccountCode,
    entityAccounts,
    vatControlAccounts,
  ]);

  useEffect(() => {
    const isSystemAccount = (account: EntityAccount) => {
      const sysAcc = SYSTEM_ACCOUNTS.find((sa) =>
        account.name
          .toLowerCase()
          .trim()
          .includes(sa.systemAccount.toLowerCase().trim())
      );
      return sysAcc ? !sysAcc.allowTransactions : false;
    };

    if (
      selectedEntityAccounts?.connectionId === data.tokenId &&
      selectedEntityAccounts?.entityAccounts?.length > 0
    ) {
      let filteredAccounts: EntityAccount[] = [];
      if (providerName == 'xero') {
        filteredAccounts = selectedEntityAccounts.entityAccounts.filter(
          (account) =>
            account.type !== AccountType.BANK &&
            account.type !== AccountType.DEPRECIATN &&
            account.type !== AccountType.DIRECTCOSTS &&
            account.type !== AccountType.EQUITY &&
            account.type !== AccountType.EXPENSE &&
            account.type !== AccountType.FIXED &&
            account.type !== AccountType.INVENTORY &&
            account.type !== AccountType.OTHERINCOME &&
            account.type !== AccountType.OVERHEADS &&
            account.type !== AccountType.PREPAYMENT &&
            account.type !== AccountType.REVENUE &&
            account.type !== AccountType.SALES &&
            account.code &&
            !isSystemAccount(account)
        );
      } else if (providerName == 'quickbooks') {
        filteredAccounts = selectedEntityAccounts.entityAccounts.filter(
          (account) =>
            account.type === 'OTHER_CURRENT_ASSET' ||
            account.type === 'OTHER_CURRENT_LIABILITY'
        );
      } else if (providerName == 'zoho') {
        filteredAccounts = selectedEntityAccounts.entityAccounts.filter(
          // what types are allowed?
          (account) =>
            ['other_current_liability', 'other_current_asset'].includes(
              account.type
            ) && !account.isSystemAccount
        );
      }

      findVatControlAccounts(selectedEntityAccounts.entityAccounts);
      setEntityAccounts(filteredAccounts);
    }
  }, [selectedEntityAccounts, data.tokenId]);

  const findVatControlAccounts = (accounts) => {
    let filteredAccounts = accounts?.filter(
      (f) =>
        f.vatControlAccountType != null &&
        ['input_tax', 'output_tax', 'input_output'].includes(
          f.vatControlAccountType
        )
    );

    if (filteredAccounts.length === 3) {
      filteredAccounts = filteredAccounts.filter(
        (f) => f.vatControlAccountType != 'input_output'
      );
    }

    if (filteredAccounts.length == 2) {
      const inputAccount = filteredAccounts.find(
        (f) => f.vatControlAccountType === 'input_tax'
      );
      const outputAccount = filteredAccounts.find(
        (f) => f.vatControlAccountType === 'output_tax'
      );

      if (inputAccount || outputAccount) {
        const iVal = inputTax || 0;
        const oVal = outputTax || 0;
        setAccountValues(inputAccount, Math.max(0, -iVal), Math.max(0, iVal));
        setAccountValues(outputAccount, Math.max(0, oVal), Math.max(0, -oVal));
      }
    } else {
      setAccountValues(filteredAccounts[0], payableAmount, receivableAmount);
    }

    setVatControlAccounts(filteredAccounts);
  };

  const setAccountValues = (account, debit, credit) => {
    if (!account) return;
    account.debit = debit || 0;
    account.credit = credit || 0;

    account.amount = account.debit - account.credit;
  };

  const updateJournalData = (updates: Partial<JournalPostData>) => {
    setJournalPostData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const handleJournalDateChange = (date: Dayjs | null) => {
    updateJournalData({
      journalDate: date ? dayjs(date).format('YYYY-MM-DD') : '',
    });
  };

  const handleNarrationChange = (value: string) => {
    updateJournalData({ narration: value });
  };

  const handleAccountChange = (value: string) => {
    const split = value?.split('~');
    const journalPostDataCopy = { ...journalPostData };
    const lines = journalPostDataCopy.lines;

    if (lines && lines?.length > 0) {
      const lastLine = lines[lines.length - 1];
      lastLine.accountId = split[0];
      lastLine.accountCode = split[1];

      setJournalPostData(journalPostDataCopy);
    }
  };

  return (
    <StyledWrapper>
      <div className="page-title">{t('vat-report.tax-submission')}</div>
      <div className="page-subtitle">
        {t('vat-report.tax-submission-will-be-saved-as-submitted')}
      </div>
      <div className="divider-20" />
      <div className="journal-info-wrapper">
        <div>
          <LabelWrapper>
            <span className="label-text">{t('vat-report.journal-date')}</span>
          </LabelWrapper>
          <FormDatePicker
            style={{ width: '100%' }}
            placeholder={t('common.select')}
            onChange={handleJournalDateChange}
            format="DD/MM/YYYY"
            value={
              journalPostData?.journalDate
                ? dayjs(journalPostData.journalDate)
                : undefined
            }
            allowClear={false}
          />
        </div>
        <div style={{ width: 450 }}>
          <LabelWrapper>
            <span className="label-text">{t('vat-report.journal-narration')}</span>
          </LabelWrapper>
          <FormInput
            width={'100%'}
            value={journalPostData?.narration || ''}
            style={{ fontWeight: 600 }}
            onChange={(e) => handleNarrationChange(e.target.value)}
          />
        </div>
        <div>
          <LabelWrapper>
            <span className="label-text">{t('vat-report.currency-narration')}</span>
          </LabelWrapper>
          <div className="currency-box">{data.currency}</div>
        </div>
      </div>
      <div className="divider-20" />
      <div className="page-subtitle" style={{ width: '100%' }}>
        {t('vat-report.would-you-like-create-manual-journal-in', {
          provider: data.providerName,
        })}
      </div>
      <table className="journal-table">
        <thead>
          <tr className="table-header">
            <th className="bb" style={{ textAlign: 'left', paddingLeft: 15 }}>
              {t('common.account')}
            </th>
            <th className="bl bb" style={{ width: 150 }}>
              {t('vat-report.debit')}
            </th>
            <th className="bl bb" style={{ width: 150 }}>
              {t('vat-report.credit')}
            </th>
          </tr>
        </thead>
        <tbody>
          {vatControlAccounts?.map((account) => (
            <>
              <tr className="table-row">
                <td
                  className="bb"
                  style={{ textAlign: 'left', paddingLeft: 15 }}
                >
                  {account.name} {account.code ? `[${account.code}]` : ''}
                </td>
                <td className="bl bb" style={{ textAlign: 'center' }}>
                  {account.debit != null ? formatNumber(account.debit) : ''}
                </td>
                <td className="bl bb" style={{ textAlign: 'center' }}>
                  {account.credit != null ? formatNumber(account.credit) : ''}
                </td>
              </tr>
            </>
          ))}
          <tr className="table-row">
            <td
              style={{
                textAlign: 'left',
                padding: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <FormSelect
                width="100%"
                height="45px"
                placeholder={t('vat-report.select-account')}
                bgcolor="var(--color-surface-raised, #FFFFFF)"
                showSearch={true}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleAccountChange}
                value={
                  journalPostData?.secondaryAccountCode
                    ? journalPostData?.secondaryAccountId +
                      '~' +
                      journalPostData?.secondaryAccountCode
                    : undefined
                }
              >
                {entityAccounts?.map((ea) => (
                  <Select.Option
                    key={ea.accountID}
                    value={ea.accountID + '~' + ea.code}
                    label={ea.name}
                    selected={
                      ea.accountID == journalPostData?.secondaryAccountId
                    }
                  >
                    {ea.name} {ea.code ? `[${ea.code}]` : ''}
                  </Select.Option>
                ))}
              </FormSelect>
              <ExportButton
                icon={<IconRefresh style={{ width: 20, height: 20 }} />}
                onClick={refreshAccounts}
                loading={loading}
              >
                {t('vat-report.refresh-acs')}
              </ExportButton>
            </td>
            <td className="bl" style={{ textAlign: 'center' }}>
              {receivableAmount != null ? formatNumber(receivableAmount) : ''}
            </td>
            <td className="bl" style={{ textAlign: 'center' }}>
              {payableAmount != null ? formatNumber(payableAmount) : ''}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="divider-20" />
      <div className="buttons">
        <div style={{ width: 150 }}>
          <ScopeButton
            onClick={() => handleSubmit(PostJournalFormAction.CANCEL)}
            width="150px"
            type="default"
          >
            {t('common.cancel')}
          </ScopeButton>
        </div>
        <div>
          <Space>
            <ScopeButton
              bg_color="#D2EDEF"
              width="fit-content"
              type="default"
              onClick={() => handleSubmit(PostJournalFormAction.SUBMIT)}
            >
              {t('vat-report.proceed-without-posting')}
            </ScopeButton>
            <ScopeButton
              loading={manualJournalPostLoading}
              onClick={validateForm}
              width="fit-content"
              type="primary"
            >
              {t('vat-report.post-manual-journal')}
            </ScopeButton>
          </Space>
        </div>
      </div>
    </StyledWrapper>
  );
};
